export const DEFAULT_API_TIMEOUT_MS = 30_000;

export class ApiRequestTimeoutError extends Error {
  readonly path: string;
  readonly timeoutMs: number;

  constructor(path: string, timeoutMs: number) {
    super(`request timeout (${timeoutMs}ms): ${path}`);
    this.name = 'ApiRequestTimeoutError';
    this.path = path;
    this.timeoutMs = timeoutMs;
  }
}

export type RequestSignal = {
  signal: AbortSignal;
  dispose: () => void;
  didTimeout: () => boolean;
};

/**
 * Combines a caller-owned AbortSignal with a timeout without relying on
 * AbortSignal.any/timeout, which are not available in every supported RN runtime.
 */
export function createRequestSignal(
  parent: AbortSignal | null | undefined,
  timeoutMs: number,
): RequestSignal {
  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const onParentAbort = () => controller.abort();
  if (parent) {
    if (parent.aborted) controller.abort();
    else parent.addEventListener('abort', onParentAbort, { once: true });
  }

  return {
    signal: controller.signal,
    didTimeout: () => timedOut,
    dispose: () => {
      clearTimeout(timer);
      if (parent) parent.removeEventListener('abort', onParentAbort);
    },
  };
}

